import labourService from './labour';
import { newUuid } from './evaluationQueue';
import type { CrewAction, CrewActionResult, OpenSegment } from '../types/labour';

/**
 * Crew-mode actions wait here until the server has them.
 *
 * Orchards have poor coverage, and a supervisor starting eight pickers cannot
 * wait for a signal. Every action is written to storage with its own
 * `client_uuid` and the time it happened BEFORE any network call; `flush`
 * sends them in order. The server is idempotent on `client_uuid`, so a flush
 * that dies half-way and runs again cannot start anyone twice, and the times
 * recorded are when the supervisor tapped, not when the signal came back.
 *
 * localStorage rather than IndexedDB: a queue is a few dozen small JSON rows,
 * and it must be readable synchronously to draw the "waiting to send" count.
 */

const KEY = 'factree_crew_action_queue_v1';

interface Queued {
  jobId: number;
  action: CrewAction;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function read(): Queued[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Queued[]) : [];
  } catch {
    return [];
  }
}

function write(items: Queued[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Storage full or blocked: the action still goes out live in this session.
  }
  listeners.forEach(l => l());
}

export function subscribeCrewQueue(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function pendingCrewActions(jobId: number): CrewAction[] {
  return read().filter(q => q.jobId === jobId).map(q => q.action);
}

/** Record an action now (with the current time) and return it. */
export function enqueueCrewAction(jobId: number, action: Omit<CrewAction, 'client_uuid' | 'at'>): CrewAction {
  const full: CrewAction = { ...action, client_uuid: newUuid(), at: new Date().toISOString() };
  write([...read(), { jobId, action: full }]);
  return full;
}

function isNetworkError(e: unknown): boolean {
  const err = e as { offline?: boolean; status?: number } | null;
  return !!err && (err.offline === true || err.status == null || err.status === 0);
}

/**
 * Send this job's queued actions. Returns the server's view of who is on the
 * job, and any action it refused (someone already finished up, a block from
 * another farm) so the page can say so. On a network failure nothing is lost
 * and `offline` is true.
 */
export async function flushCrewQueue(jobId: number): Promise<{
  open: OpenSegment[] | null; rejected: CrewActionResult[]; offline: boolean;
}> {
  const mine = pendingCrewActions(jobId);
  if (!mine.length) {
    try {
      return { open: await labourService.getCrew(jobId), rejected: [], offline: false };
    } catch (e) {
      if (isNetworkError(e)) return { open: null, rejected: [], offline: true };
      throw e;
    }
  }
  try {
    const res = await labourService.sendCrewActions(jobId, mine);
    const done = new Set(res.results.map(r => r.client_uuid));
    write(read().filter(q => !done.has(q.action.client_uuid)));
    return { open: res.open, rejected: res.results.filter(r => r.status === 'rejected'), offline: false };
  } catch (e) {
    if (isNetworkError(e)) return { open: null, rejected: [], offline: true };
    throw e;
  }
}
